import * as React from "react";
import { useEffect, useState } from "react";
import PopUp from "../../popup/PopUp";
import { useStyles } from "../../../assets/styles/Theme";
import { useTheme } from "@mui/material/styles";
import { Box, Typography, Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Document, Page, pdfjs } from "react-pdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import workerSrc from "pdfjs-dist/build/pdf.worker.entry";

pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

interface ImagePopUpProps {
  open: boolean;
  handleClose: () => void;
  image?: string | { fileUrl?: string; fileName?: string } | any;
  name?: string;
  description?: string;
  objectUrl?: string;
}

const ImagePopUp: React.FC<ImagePopUpProps> = ({
  open,
  handleClose,
  image,
  name = "No preview available",
  description = "No Description",
  objectUrl,
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [fileUrl, setFileUrl] = useState<string>("");
  const [numPages, setNumPages] = useState<number | null>(null);
  const [isImage, setIsImage] = useState<boolean>(false);

  // console.log("image objectUrl", objectUrl);

  // Unified file source from either image or objectUrl
  const fileSource =
    objectUrl || (typeof image === "object" && image?.fileUrl) || image || "";
  const fileName =
    name || (typeof image === "object" && image?.fileName) || "downloaded-file";

  // Detect file types
  const isPdf =
    fileSource.toLowerCase().includes(".pdf") ||
    fileSource.startsWith("data:application/pdf") ||
    fileSource.startsWith("blob:");

  const isExcel =
    fileSource.toLowerCase().endsWith(".xlsx") ||
    fileSource.toLowerCase().endsWith(".xls");

  useEffect(() => {
    checkIfImage(fileSource);
  }, [fileSource]);

  async function checkIfImage(fileSource: string): Promise<void> {
    const result =
      fileSource.startsWith("data:image") ||
      /\.(jpeg|jpg|png|gif|webp)$/i.test(fileSource) ||
      (fileSource.startsWith("blob:") && (await isBlobUrlImage(fileSource)));
    setIsImage(result);
  }

  async function isBlobUrlImage(blobUrl: string): Promise<boolean> {
    try {
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      const isimg = blob.type.startsWith("image/");
      console.log("Blob Type:", blob.type);
      return isimg;
    } catch (error) {
      console.error("Failed to fetch blob:", error);
      return false;
    }
  }

  useEffect(() => {
    if (isPdf && image) {
      setFileUrl(image);
    } else {
      setFileUrl("");
    }
  }, [image, isPdf]);

  const content = (
    <>
      <Box>
        <Grid container spacing={2} mt={0}>
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: theme.palette.background.default,
                height: "300px",
              }}
            >
              {isImage ? (
                <img
                  src={fileSource}
                  alt={fileName}
                  style={{ maxWidth: "100%", maxHeight: "300px" }}
                />
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <InsertDriveFileIcon style={{ fontSize: 80 }} />
                  <Typography variant="body2">No Preview Available</Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
            textAlign={"center"}
          >
            <Typography variant={"subtitle_m" as any} align="center">
              {image?.fileName}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </>
  );

  const actions = (
    <>
      {objectUrl || image ? (
        <a
          href={fileSource}
          download={fileName}
          target="_blank"
          rel="open noreferrer"
        >
          <Button variant="outlined" sx={styles.btn_secondary}>
            Download
          </Button>
        </a>
      ) : isExcel ? (
        // For Excel files, pass the URL to the download button
        <a href={image} download={name || "downloaded-file"}>
          <Button variant="outlined" sx={styles.btn_secondary}>
            Download Excel
          </Button>
        </a>
      ) : (
        <Button variant="outlined" sx={styles.btn_secondary} disabled>
          No File to Download
        </Button>
      )}
    </>
  );

  return (
    <PopUp
      open={open}
      handleClose={handleClose}
      title="Attachment"
      content={content}
      actions={actions}
      maxWidth="sm"
    />
  );
};

export default ImagePopUp;
